import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import maestroService from '../maestro-contabil/maestroService';

interface Props {
  clienteId: number;
  nomeCliente: string;
  ativo: boolean;
}

type SetupStatus = { status: string; percentualConcluido: number; etapasConcluidas: number; etapasTotais: number };

const ClientesMaestroTab = ({ clienteId, nomeCliente, ativo }: Props) => {
  const [status, setStatus] = useState<SetupStatus | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [resetando, setResetando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!ativo || clienteId <= 0) return;
    setCarregando(true);
    setErro(null);
    maestroService.obterSetupStatusBatch([clienteId])
      .then(lista => setStatus(lista[0] ?? { status: 'NAO_INICIADO', percentualConcluido: 0, etapasConcluidas: 0, etapasTotais: 8 }))
      .catch(() => setErro('Não foi possível carregar o status do Maestro Contábil (é preciso estar logado como Master).'))
      .finally(() => setCarregando(false));
  }, [ativo, clienteId]);

  async function resetar() {
    const { value: justificativa, isConfirmed } = await Swal.fire({
      title: 'Resetar Setup do Maestro Contábil',
      html: `
        <p style="font-size:14px;color:#7e8299;margin-bottom:8px">
          Esta ação irá reiniciar o processo de configuração inicial do Maestro Contábil para <strong>${nomeCliente}</strong>.
        </p>
        <p style="font-size:13px;color:#f64e60;margin-bottom:16px">
          ⚠️ Ciclos e atividades já existentes não são afetados — só a configuração inicial (as 8 etapas) é zerada.
        </p>
        <label style="display:block;font-size:13px;font-weight:600;text-align:left;margin-bottom:4px">
          Justificativa (obrigatória):
        </label>
        <textarea id="mc-reset-justificativa" class="swal2-textarea" placeholder="Descreva o motivo do reset da configuração..." rows="3" style="width:100%;box-sizing:border-box"></textarea>
      `,
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Confirmar Reset',
      reverseButtons: true,
      customClass: { confirmButton: 'btn btn-modal-excluir ms-5', cancelButton: 'btn btn-modal-cancelar' },
      buttonsStyling: false,
      preConfirm: () => {
        const val = (document.getElementById('mc-reset-justificativa') as HTMLTextAreaElement)?.value?.trim();
        if (!val) { Swal.showValidationMessage('A justificativa é obrigatória.'); return false; }
        return val;
      },
    });

    if (!isConfirmed || !justificativa) return;

    setResetando(true);
    try {
      await maestroService.resetarSetupPorCliente(clienteId, justificativa as string);
      setStatus({ status: 'AGUARDANDO_CONFIGURACAO', percentualConcluido: 0, etapasConcluidas: 0, etapasTotais: 8 });
      Swal.fire({ text: 'Setup do Maestro Contábil resetado. O cliente poderá configurar tudo novamente.', icon: 'success', timer: 3500, showConfirmButton: false });
    } catch {
      Swal.fire({ text: 'Erro ao resetar o setup do Maestro Contábil — confirme que está logado como Master.', icon: 'error', timer: 3500, showConfirmButton: false });
    } finally {
      setResetando(false);
    }
  }

  if (!ativo) return null;

  return (
    <div style={{ padding: '10px' }}>
      <div className='card card-custom card-stretch'>
        <div className='card-header border-0 pt-4'>
          <h4 className='card-title fw-bolder text-dark'>Maestro Contábil — Setup</h4>
        </div>

        <div className='card-body pt-2'>
          {erro && <div className='alert alert-danger py-2 px-4 mb-4'>{erro}</div>}

          {carregando && (
            <div className='text-center py-8'><span className='spinner-border text-primary' /></div>
          )}

          {!carregando && status && (
            <>
              <div className='row g-3 mb-5'>
                <div className='col-md-4'>
                  <div className='card border-0 bg-light p-4 text-center h-100'>
                    <div className='text-muted fs-8 mb-2'>Status</div>
                    <div>
                      {status.status === 'ATIVO'
                        ? <span className='badge badge-light-success'>Ativo</span>
                        : status.etapasConcluidas === 0
                          ? <span className='badge badge-light-secondary'>Não iniciado</span>
                          : <span className='badge badge-light-warning'>Em configuração</span>}
                    </div>
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className='card border-0 bg-light p-4 text-center h-100'>
                    <div className='text-muted fs-8 mb-2'>Etapas concluídas</div>
                    <div className='fw-bolder fs-4'>{status.etapasConcluidas} / {status.etapasTotais}</div>
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className='card border-0 bg-light p-4 text-center h-100'>
                    <div className='text-muted fs-8 mb-2'>Progresso</div>
                    <div className='fw-bolder fs-4'>{status.percentualConcluido}%</div>
                  </div>
                </div>
              </div>

              <div className='separator my-4' />
              <h6 className='fw-bold mb-2'>Resetar configuração</h6>
              <p className='text-muted fs-8 mb-3'>
                Zera as 8 etapas do wizard de Setup do Maestro Contábil para este cliente — ciclos, atividades, riscos e demais dados operacionais já criados não são afetados.
              </p>
              <button type='button' className='btn btn-danger' disabled={resetando} onClick={resetar}>
                {resetando ? <span className='spinner-border spinner-border-sm me-2' /> : <i className='bi bi-arrow-counterclockwise me-2' />}
                Resetar setup do Maestro Contábil
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientesMaestroTab;
